/* eslint-disable @typescript-eslint/no-explicit-any */
import * as Sentry from "@sentry/nextjs";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { HmacSHA256 } from "crypto-js";
import dayjs from "dayjs";

import { Oriented } from "@/data/constants/oriented";
import { NIKE_URL } from "@/data/constants/url";
import { links } from "@/data/links";
import { useAuth } from "@/features/auth/shared/hooks/use-auth";
import { useLang } from "@/hooks/use-lang";
import { Lang } from "@/i18n-config";
import { WGPTMockBody, WGPTMockHeaders } from "@/mock/wgpt-mock-data";

import { SessionData } from "../../historical-dialog-sidebar/services/get-session-list-data";
import { getChartParamsList } from "../helpers/reply-charts/get-chart-params-list";
import { useStreamActions } from "../hooks/use-stream-actions";

export type ChatPayload = Record<string, unknown> & {
  query: string;
  investStyle: Oriented | null;
  sessionId?: string;
};

export type ProcessingStatus = "pending" | "processing" | "done";

export type ProcessingStatusData = {
  name: string;
  status: ProcessingStatus;
  percentage?: number;
};

type ChatStreamParams = {
  lang: Lang;
  token: string;
  streamActions: ReturnType<typeof useStreamActions>;
  mode: ChatMode;
};

export const chatStream = ({
  lang,
  token,
  streamActions,
  mode,
}: ChatStreamParams) => {
  return async (payload: ChatPayload) => {
    try {
      const { sessionId, ...body } = payload;
      const endpointMap = {
        local: process.env.NEXT_PUBLIC_WGPT_LOCAL_URL ?? "",
        mock: process.env.NEXT_PUBLIC_WGPT_MOCK_URL ?? "",
        normal: `${NIKE_URL}/winwin-gpt/chat${
          sessionId ? `/${sessionId}` : ""
        }`,
      };
      const endpoint = endpointMap[mode] ?? endpointMap.normal;

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-User-Profile": token,
          "Accept-Language": lang,
          ...(mode !== "normal" ? WGPTMockHeaders : {}),
        },
        body: JSON.stringify({
          ...body,
          ...(mode === "local" ? WGPTMockBody : {}),
        }),
      })
        .then((response) => {
          if (response.status >= 400) {
            // Error handling #1
            throw new Error("Bad response from server");
          }
          return response.body;
        })
        .then((body) => {
          const reader = body?.getReader();
          let isReplyDone = false;
          let response = "";
          let bufferedChunk = "";
          const temporaryMessageId = HmacSHA256(
            payload.query + dayjs().format("YYYY-MM-DD HH:mm:ss"),
            "secret"
          ).toString();

          // initial gpt message
          streamActions.initMessage({ messageId: temporaryMessageId });

          return new ReadableStream({
            start(controller) {
              function push() {
                reader
                  ?.read()
                  .then(({ done, value }) => {
                    if (done) {
                      if (!isReplyDone) {
                        // Error handling #2
                        throw new Error("stream ended before replyDone");
                      }
                      controller.close();
                      return;
                    }

                    const decoder = new TextDecoder();
                    const text = decoder.decode(value);

                    text
                      .split("%z3mQ1k;")
                      .filter(Boolean)
                      .forEach((textChunk) => {
                        let data: any;
                        try {
                          data = JSON.parse(bufferedChunk + textChunk);
                          bufferedChunk = "";
                        } catch (e) {
                          console.error("merge chunk error", e);
                          bufferedChunk += textChunk;
                          return; // for uncompleted chunk, return to continue reading the stream
                        }

                        if (data.type === "replyChunk") {
                          response += data.payload;
                          const encoder = new TextEncoder();
                          const uint8Array = encoder.encode(data.payload);
                          controller.enqueue(uint8Array);

                          streamActions.handleStreamChunk({
                            chunk: response,
                            messageId: temporaryMessageId,
                          });
                        } else if (data.type === "jobStatusUpdate") {
                          // handle job status
                        } else if (data.type === "processingStatus") {
                          streamActions.handleProcessingStatus({
                            status: data.payload.steps,
                            messageId: temporaryMessageId,
                          });
                        } else if (data.type === "replyDone") {
                          isReplyDone = true;
                          streamActions.handleCompleteStream({
                            messageId: data.payload.messageId,
                          });
                        } else if (data.type === "error") {
                          // Error handling #3
                          if (data.payload.errorStage === "finalOutput") {
                            streamActions.handleOutputError({
                              messageId: temporaryMessageId,
                              query: payload.query,
                              investStyle: payload.investStyle,
                            });
                          } else if (data.payload.errorStage === "streaming") {
                            streamActions.handleStreamError({
                              query: payload.query,
                              investStyle: payload.investStyle,
                            });
                          }
                        } else if (data.type === "replyChart") {
                          const chartParamsList = getChartParamsList(
                            data.payload
                          );
                          streamActions.handleReplyChart({
                            chartParamsList,
                          });
                        }
                      });
                    push();
                  })
                  .catch((e) => {
                    console.error("stream close error", e);
                    controller.error(e);
                    Sentry.captureException(e);
                  });
              }
              push();
            },
          });
        })
        .then((stream) => {
          return new Response(stream, {
            headers: { "Content-Type": "text/html" },
          }).text();
        })
        .then((result) => {
          return result;
        })
        .catch((e) => {
          // Error handling #4
          console.error("fetch error", e);
          Sentry.captureException(e);
          streamActions.handleStreamingInterruptionError({
            query: payload.query,
            investStyle: payload.investStyle,
          });
        })
        .finally(() => {
          window.history.replaceState({}, "", window.location.pathname);
        });
      return response;
    } catch (error) {
      Sentry.captureException(error);
    }
  };
};

type ChatMode = "local" | "mock" | "normal";

export const useChatStream = () => {
  const { t, lang } = useLang();
  const streamActions = useStreamActions(t);
  const queryClient = useQueryClient();
  const { backendToken } = useAuth();

  return useMutation(
    chatStream({
      lang,
      token: backendToken ?? "",
      streamActions,
      mode: process.env.NEXT_PUBLIC_WGPT_X_CHAT_MODE as ChatMode,
    }),
    {
      onSuccess: async (_, variables) => {
        await queryClient.invalidateQueries(["wgpt-quota", lang]);
        await queryClient.invalidateQueries(["session-list", lang]);
        await queryClient.invalidateQueries(["session", variables.sessionId]);

        // if sessionId is not provided, navigate to the session
        if (!variables.sessionId) {
          const sessionData = queryClient.getQueryData([
            "session-list",
            lang,
          ]) as SessionData[];
          const sessionId = sessionData?.[0]?.sessionId;
          if (sessionId) {
            window.history.pushState(
              {},
              "",
              `/${lang}${links.myWinwingptQuestion({ id: sessionId })}`
            );
          }
        }
      },
      onError: (error) => {
        Sentry.captureException(error);
      },
    }
  );
};
